import React, { useCallback, useEffect, useMemo, useRef } from "react";

import { faLocationArrow } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { bem } from "@react-md/utils";
import Col from "antd/es/col";
import Row from "antd/es/row";
import { DefaultOptionType } from "antd/es/select";
import Tooltip from "antd/es/tooltip";
import cn from "classnames";

import CountryCode from "@mapmycustomers/shared/enum/CountryCode";
import Address from "@mapmycustomers/shared/types/Address";
import { GeocodeResult } from "@mapmycustomers/shared/types/base/Located";
import LongLat from "@mapmycustomers/shared/types/base/LongLat";
import Region from "@mapmycustomers/shared/types/Region";
import { isEmptyString } from "@mapmycustomers/shared/util/stringUtils";

import Button from "../../../Button";
import { useConfigProvider } from "../../../ConfigProvider";
import LoadingSpinner from "../../../LoadingSpinner";
import layout from "../../../styles/layout";
import ErrorRow from "../../ErrorRow";
import Labeled, { LabeledProps } from "../../Labeled";
import SelectField, { SelectFieldProps } from "../../SelectField";
import TextField, { TextFieldProps } from "../../TextField";
import useCountryListOptions from "../utils/useCountryListOptions";

import RegionField from "./RegionField";
const block = bem("mmc-manual-address");

export interface ManualAddressProps extends Omit<LabeledProps, "children"> {
  caption?: string;
  disabled?: boolean;
  editMode?: boolean;
  error?: string;
  onChange?: (address?: Address) => void;
  onReverseGeocodeAddress: (payload: {
    callback: (result: GeocodeResult) => void;
    coordinates: LongLat;
    failureCallback?: () => void;
  }) => void;
  onToggleManualAddress?: () => void;
  value?: Address;
}

const ManualAddress = ({
  caption,
  disabled,
  error,
  labelClassName,
  onChange,
  onReverseGeocodeAddress,
  onToggleManualAddress,
  required,
  value,
  ...labelProps
}: ManualAddressProps) => {
  const { formatMessage, useFindMyLocation } = useConfigProvider();

  const handleAddressChange = useCallback<NonNullable<TextFieldProps["onChange"]>>(
    (address) => onChange?.({ ...value, address }),
    [onChange, value]
  );

  const handleCountryCodeChange = useCallback<
    NonNullable<SelectFieldProps<CountryCode>["onChange"]>
  >(
    (countryCode, option) => {
      onChange?.({
        ...value,
        country: option ? (option as DefaultOptionType).text : null,
        countryCode: countryCode ?? null,
        region: undefined,
        regionCode: undefined,
      });
    },
    [onChange, value]
  );

  const handleCountryClear = useCallback(() => {
    onChange?.({
      ...value,
      country: undefined,
      countryCode: undefined,
    });
  }, [onChange, value]);

  const handleCityChange = useCallback(
    (city: string) => onChange?.({ ...value, city }),
    [onChange, value]
  );

  const handleRegionChange = useCallback(
    (region: Region) => onChange?.({ ...value, ...region }),
    [onChange, value]
  );

  const handlePostalCodeChange = useCallback(
    (postalCode: string) => onChange?.({ ...value, postalCode }),
    [onChange, value]
  );

  // Find Me functionality:
  const [, , findMeLoading, findMeError, handleFindMyLocation, geocodingResult, resetError] =
    useFindMyLocation(onReverseGeocodeAddress);
  const lastGeocodingResult = useRef<GeocodeResult>();

  const canClearCountry = useMemo(
    () =>
      !isEmptyString(value?.countryCode) &&
      isEmptyString(value?.address) &&
      isEmptyString(value?.city) &&
      isEmptyString(value?.region) &&
      isEmptyString(value?.postalCode),
    [value]
  );

  useEffect(() => {
    resetError();
  }, [value, resetError]);

  useEffect(() => {
    if (lastGeocodingResult.current !== geocodingResult) {
      lastGeocodingResult.current = geocodingResult;
      onChange?.(geocodingResult?.address);
    }
  }, [geocodingResult, onChange]);

  const countryListOptions = useCountryListOptions();

  return (
    <Labeled
      labelClassName={cn(block("label"), labelClassName)}
      required={required}
      {...labelProps}
    >
      <Row gutter={[layout.spacerS, layout.spacerS]}>
        <Col span={24}>
          <TextField
            error={findMeError}
            locked={disabled}
            onChange={handleAddressChange}
            placeholder={formatMessage("ui.manualAddress.street")}
            suffix={
              findMeLoading ? (
                <LoadingSpinner mini />
              ) : !disabled ? (
                <Tooltip title={formatMessage("ui.address.findMe.tooltip")}>
                  <FontAwesomeIcon
                    className={block("icon")}
                    icon={faLocationArrow}
                    onClick={handleFindMyLocation}
                    size="lg"
                  />
                </Tooltip>
              ) : undefined
            }
            value={value?.address}
          />
        </Col>
        <Col span={24}>
          <SelectField
            allowClear={canClearCountry}
            className={block("country-dropdown")}
            dropdownMatchSelectWidth={false}
            locked={disabled}
            onChange={handleCountryCodeChange}
            onClear={handleCountryClear}
            optionFilterProp="text"
            options={countryListOptions}
            placeholder={formatMessage("ui.manualAddress.country")}
            showSearch
            value={value?.countryCode}
          />
        </Col>
        <Col span={12}>
          <TextField
            locked={disabled}
            onChange={handleCityChange}
            placeholder={formatMessage("ui.manualAddress.city")}
            value={value?.city}
          />
        </Col>
        <Col span={12}>
          <RegionField
            countryCode={value?.countryCode}
            disabled={disabled}
            onChange={handleRegionChange}
            value={value}
          />
        </Col>
        <Col span={24}>
          <TextField
            locked={disabled}
            onChange={handlePostalCodeChange}
            placeholder={formatMessage("ui.manualAddress.postalCode")}
            value={value?.postalCode}
          />
        </Col>
        {!!onToggleManualAddress && !disabled && (
          <Col span={24}>
            <Button
              className={block("search-manual-button")}
              onClick={onToggleManualAddress}
              type="link"
            >
              {formatMessage("ui.manualAddress.searchGoogle")}
            </Button>
          </Col>
        )}
      </Row>
      {error ? (
        <ErrorRow>{error}</ErrorRow>
      ) : caption ? (
        <div className={block("caption")}>{caption}</div>
      ) : null}
    </Labeled>
  );
};

export default ManualAddress;
